<template>
  <div :class="b('', { isActive })">
    <c-main-navigation-icon-button
      :aria-expanded="isActive"
      aria-controls="search-container"
      :aria-label="$t('c-embedded-niko-search.button')"
      @click="onSearchToggleClick"
    >
      <e-icon
        :icon="!isActive ? 'i-search' : 'i-close'"
        :size="!isActive ? '22' : '18'"
      />
    </c-main-navigation-icon-button>

    <div
      id="search-container"
      :class="b('embed-container', { isActive })"
      role="dialog"
      :aria-hidden="!isActive"
      :aria-label="$t('c-embedded-niko-search.overlay')"
    >
      <niko-autocomplete :locale="sessionStore.searchLocale">
        <div class="niko-ac-search-panel">
          <div class="niko-ac-search-panel__container">
            <h1>
              {{ $t('c-search-embed.heading') }}
            </h1>
            <div id="autocomplete" />
          </div>
        </div>
      </niko-autocomplete>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import useSessionStore from '@/stores/session';
  import cMainNavigationIconButton from '@/components/c-main-navigation-icon-button.vue';
  import eIcon from '@/elements/e-icon.vue';

  const LOCK_SCROLL_CLASS = 'c-embedded-niko-search__lock-scroll';
  const NIKO_SEARCH_BASE_URL = 'https://nkdsprdshared.blob.core.windows.net/nikosearch';

  type Setup = {
    sessionStore: ReturnType<typeof useSessionStore>;
  };

  type Data = {
    isActive: boolean;
  };

  /**
   * Embeds the niko autocomplete search app
   */
  export default defineComponent({
    name: 'c-embedded-niko-search',

    components: {
      cMainNavigationIconButton,
      eIcon,
    },

    // props: {},
    // emits: {},

    setup(): Setup {
      return {
        sessionStore: useSessionStore(),
      };
    },

    data(): Data {
      return {
        isActive: false,
      };
    },

    // computed: {},

    watch: {
      isActive: {
        handler(newVal) {
          if (newVal) {
            document.body.classList.add(LOCK_SCROLL_CLASS);
            // We want to show the scroll bar even if not needed to prevent layout shifts
            document.documentElement.style.overflowY = 'scroll';
          } else {
            document.body.classList.remove(LOCK_SCROLL_CLASS);
          }
        },
        immediate: true,
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      const link = document.createElement('link');

      link.rel = 'stylesheet';
      link.href = `${NIKO_SEARCH_BASE_URL}/css/NikoAutocompleteWebcomponent.726768885ba281535aca.css`;
      document.head.append(link);

      const script = document.createElement('script');

      script.defer = true;
      script.src = `${NIKO_SEARCH_BASE_URL}/js/NikoAutocompleteWebcomponent.947e85f380fcf957b28a.js`;
      document.body.append(script);
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      onSearchToggleClick(): void {
        this.isActive = !this.isActive;

        if (this.isActive) {
          this.$nextTick(() => {
            const firstFocusableElement = this.$el.querySelector('.niko-ac-search-panel');

            if (firstFocusableElement) {
              const nestedInputElement = firstFocusableElement.querySelector('input');

              if (nestedInputElement && nestedInputElement.value.trim() !== '') {
                // retriggers search if the input field has a search term on reopening
                nestedInputElement.focus();
              } else {
                firstFocusableElement.focus();
              }
            }
          });
        }
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  .c-embedded-niko-search {
    &:hover,
    &--is-active {
      background: var(--theme-color-grayscale--200);
    }

    &__embed-container {
      width: 100%;
      display: none;
      box-shadow: 0 5px 15px rgba(var(--theme-color-grayscale--200-rgb), 0.6);
      padding: 0 1rem;

      &--is-active {
        background: var(--theme-color-grayscale--200);
        display: block;
        position: fixed;
        top: var(--theme-header-height);
        left: 0;
      }
    }

    &__lock-scroll {
      overflow: hidden;
    }
  }

  // Special styling for the result overlay of c-embedded-niko-search
  /* stylelint-disable-next-line selector-class-pattern */
  .aa-Input {
    height: 50px;
  }

  /* stylelint-disable-next-line selector-class-pattern */
  .aa-Panel {
    // Force z-index below c-header
    /* stylelint-disable-next-line declaration-no-important */
    z-index: 9 !important;

    /* stylelint-disable-next-line selector-class-pattern, selector-pseudo-element-colon-notation */
    &:after {
      content: '';
      background-color: var(--theme-color-grayscale--1000);
      width: 100vw;
      height: 100vh;
      position: fixed;
      top: 0;
      left: 0;
      z-index: 1;
    }

    img {
      width: auto;
      height: auto;
    }

    mark {
      background-color: var(--theme-search-embed-aa-panel-mark-bg);
      color: var(--theme-color-grayscale--0);
    }

    /* stylelint-disable-next-line selector-class-pattern */
    .aa-PanelLayout {
      z-index: 2;
      margin-top: 1rem;
    }

    .niko-ac-btn {
      padding: 1rem 1.5rem;
    }
  }
</style>
